import {
  Wifi, Snowflake, Waves, Car, Tv, ChefHat, WashingMachine,
  Wind, Zap, ShieldCheck, Utensils, Sofa, Bath, TreePine, Refrigerator,
} from "lucide-react";

// Registry mapping equipment keys -> {icon, label}.
// Keys are stable strings stored in the DB; labels can evolve without migration.
export const EQUIPEMENTS_CATALOG = {
  wifi:            { icon: Wifi,           label: "Wifi" },
  climatisation:   { icon: Snowflake,      label: "Climatisation" },
  ventilateur:     { icon: Wind,           label: "Ventilateur" },
  piscine:         { icon: Waves,          label: "Piscine" },
  parking:         { icon: Car,            label: "Parking" },
  tv:              { icon: Tv,             label: "TV" },
  cuisine_equipee: { icon: ChefHat,        label: "Cuisine equipee" },
  lave_linge:      { icon: WashingMachine, label: "Lave-linge" },
  seche_cheveux:   { icon: Bath,           label: "Seche-cheveux" },
  frigo:           { icon: Refrigerator,   label: "Frigo" },
  generateur:      { icon: Zap,            label: "Generateur" },
  securite:        { icon: ShieldCheck,    label: "Securite 24/7" },
  restauration:    { icon: Utensils,       label: "Restauration" },
  salon:           { icon: Sofa,           label: "Salon meuble" },
  jardin:          { icon: TreePine,       label: "Jardin" },
};

export const EQUIPEMENT_KEYS = Object.keys(EQUIPEMENTS_CATALOG);

/**
 * Compact badge grid for the equipment list on cards and detail pages.
 *
 * Props:
 *  - keys: string[] — equipment keys to display
 *  - variant: "compact" (default, tight card-friendly) | "full" (larger with labels)
 *  - max: number | undefined — cap at N items and show "+X" pill for the rest
 *  - className: extra classes
 */
export default function EquipementsBadges({ keys = [], variant = "compact", max, className = "" }) {
  const list = Array.isArray(keys) ? keys.filter((k) => EQUIPEMENTS_CATALOG[k]) : [];
  if (list.length === 0) return null;
  const visible = typeof max === "number" ? list.slice(0, max) : list;
  const overflow = typeof max === "number" ? Math.max(0, list.length - max) : 0;

  if (variant === "full") {
    return (
      <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${className}`} data-testid="equipements-full">
        {list.map((k) => {
          const { icon: Icon, label } = EQUIPEMENTS_CATALOG[k];
          return (
            <div key={k} className="flex items-center gap-2 p-3 rounded-2xl border border-emerald-100 bg-emerald-50/50" data-testid={`equipement-${k}`}>
              <div className="w-9 h-9 rounded-xl bg-white grid place-items-center text-emerald-700 flex-shrink-0">
                <Icon size={16} />
              </div>
              <span className="text-sm font-medium text-[var(--ink)]">{label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // compact
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid="equipements-compact">
      {visible.map((k) => {
        const { icon: Icon, label } = EQUIPEMENTS_CATALOG[k];
        return (
          <span
            key={k}
            title={label}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[11px] font-medium"
            data-testid={`equipement-badge-${k}`}
          >
            <Icon size={12} />
            <span className="hidden sm:inline">{label}</span>
          </span>
        );
      })}
      {overflow > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[var(--line)] text-[var(--ink-soft)] text-[11px] font-medium">
          +{overflow}
        </span>
      )}
    </div>
  );
}
